import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import Image from 'next/image';
import React from 'react';

import { SOLANA_CHAIN_IDS } from '@/config/solana';
import { STELLAR_CHAIN_IDS, STELLAR_NETWORKS } from '@/config/stellar';
import {
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/ui';
import { supportedMainnetsChainIds, supportedTestnetsChainIds } from '@/utils/networksConfig';

import { HARDCODED_PRIORITY_CHAIN_IDS_FOR_NETWORK_SELECTOR } from '../../constants/networkRelatedConstants';

interface NetworkSelectorProps {
  name?: string;
  value?: string | number | null;
  error?: boolean;
  onChange?: (e: React.ChangeEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
  label?: string;
  networkList?: number[];
  testnetNetworkList?: number[];
  includeSolana?: boolean;
  includeStellar?: boolean;
  disabled?: boolean;
}

export const NetworkSelector = ({
  name,
  value = null,
  error,
  onChange: handleChange,
  onBlur: handleBlur,
  label = 'Payout Network',
  networkList,
  testnetNetworkList,
  includeSolana = false,
  includeStellar = false,
  disabled,
}: NetworkSelectorProps) => {
  const { networkConfigurations } = useDynamicContext();
  const supportedEvmNetworks = networkConfigurations?.['evm'];
  const filteredMainnetNetworks = supportedEvmNetworks?.filter((network) => {
    const networks = networkList || supportedMainnetsChainIds;
    return networks.includes(network.chainId as number);
  });

  const filteredTestnetNetworks = supportedEvmNetworks?.filter((network) => {
    const networks = testnetNetworkList || supportedTestnetsChainIds;
    return networks.includes(network.chainId as number);
  });

  const priorityChainIds = HARDCODED_PRIORITY_CHAIN_IDS_FOR_NETWORK_SELECTOR;
  const reorderedMainnetNetworks = filteredMainnetNetworks
    ? (() => {
        const priority = priorityChainIds
          .map((chainId: number) => filteredMainnetNetworks.find((network: any) => network.chainId === chainId))
          .filter((network): network is NonNullable<typeof network> => Boolean(network));
        const rest = filteredMainnetNetworks
          .filter((network: any) => !priorityChainIds.includes(network.chainId as number))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        return [...priority, ...rest];
      })()
    : filteredMainnetNetworks;

  const solanaNetworkOptions = includeSolana
    ? [
        {
          chainId: SOLANA_CHAIN_IDS.MAINNET,
          name: 'Solana',
          vanityName: 'Solana',
          iconUrl: '/assets/webp/solana.webp',
          isTestnet: false,
        },
        {
          chainId: SOLANA_CHAIN_IDS.FOGO,
          name: 'Fogo',
          vanityName: 'Fogo',
          iconUrl: '/assets/webp/fogo.webp',
          isTestnet: false,
        },
        {
          chainId: SOLANA_CHAIN_IDS.DEVNET,
          name: 'Solana Devnet',
          vanityName: 'Solana Devnet',
          iconUrl: '/assets/webp/solana.webp',
          isTestnet: true,
        },
      ]
    : [];

  const stellarNetworkOptions = includeStellar
    ? [STELLAR_CHAIN_IDS.MAINNET, STELLAR_CHAIN_IDS.TESTNET]
        .map((chainId) => STELLAR_NETWORKS[chainId])
        .map((network) => ({
          chainId: network.chainId,
          name: network.name,
          vanityName: network.name,
          iconUrl: '/assets/webp/stellar.webp',
          isTestnet: network.isTestnet,
        }))
    : [];

  const solanaMainnets = solanaNetworkOptions.filter((n) => !n.isTestnet);
  const solanaTestnets = solanaNetworkOptions.filter((n) => n.isTestnet);
  const stellarMainnets = stellarNetworkOptions.filter((n) => !n.isTestnet);
  const stellarTestnets = stellarNetworkOptions.filter((n) => n.isTestnet);

  const hasEvmMainnets = reorderedMainnetNetworks && reorderedMainnetNetworks.length > 0;
  const hasSvmMainnets = solanaMainnets.length > 0;
  const hasStellarMainnets = stellarMainnets.length > 0;
  const hasEvmTestnets = filteredTestnetNetworks && filteredTestnetNetworks.length > 0;
  const hasSvmTestnets = solanaTestnets.length > 0;
  const hasStellarTestnets = stellarTestnets.length > 0;

  const handleValueChange = (val: string) => {
    if (handleChange) {
      const syntheticEvent = {
        target: { name, value: val === '' ? '' : Number(val) || val },
      } as React.ChangeEvent<HTMLInputElement>;
      handleChange(syntheticEvent);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <Label htmlFor={`network-selector-${name}`}>{label}</Label>
      <Select
        name={name}
        value={value != null ? String(value) : ''}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={`network-selector-${name}`}
          className={error ? 'tw:border-destructive' : undefined}
          onBlur={handleBlur}
        >
          <SelectValue placeholder="Select network" />
        </SelectTrigger>
        <SelectContent>
          {(hasEvmMainnets || hasSvmMainnets || hasStellarMainnets) && (
            <SelectGroup>
              <SelectLabel>MAINNET</SelectLabel>
              {hasEvmMainnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">EVM</SelectLabel>
                  {reorderedMainnetNetworks?.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        {network.iconUrls[0] ? (
                          <Image src={network.iconUrls[0]} width={20} height={20} alt={network.name} />
                        ) : (
                          <div className="tw:w-5 tw:h-5" />
                        )}
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
              {hasSvmMainnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">SVM</SelectLabel>
                  {solanaMainnets.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        <Image src={network.iconUrl} width={20} height={20} alt={network.name} />
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
              {hasStellarMainnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">
                    Stellar
                  </SelectLabel>
                  {stellarMainnets.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        <Image src={network.iconUrl} width={20} height={20} alt={network.name} />
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectGroup>
          )}
          {(hasEvmTestnets || hasSvmTestnets || hasStellarTestnets) && (
            <SelectGroup>
              <SelectLabel>TESTNET</SelectLabel>
              {hasEvmTestnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">EVM</SelectLabel>
                  {filteredTestnetNetworks?.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        {network.iconUrls[0] ? (
                          <Image src={network.iconUrls[0]} width={20} height={20} alt={network.name} />
                        ) : (
                          <div className="tw:w-5 tw:h-5" />
                        )}
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
              {hasSvmTestnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">SVM</SelectLabel>
                  {solanaTestnets.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        <Image src={network.iconUrl} width={20} height={20} alt={network.name} />
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
              {hasStellarTestnets && (
                <>
                  <SelectLabel className="tw:text-xs tw:pl-6 tw:font-normal tw:text-muted-foreground">
                    Stellar
                  </SelectLabel>
                  {stellarTestnets.map((network) => (
                    <SelectItem key={network.chainId} value={String(network.chainId)}>
                      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                        <Image src={network.iconUrl} width={20} height={20} alt={network.name} />
                        <span className="tw:capitalize">{network.vanityName ?? network.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
      {error && <p className="tw:text-sm tw:text-destructive">{error}</p>}
    </div>
  );
};
